import { AnimationEvent, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { goBack } from '../../util/go_back.ts';
import { InnerPageContent, OuterPageContent } from '../../util/shared.tsx';
import { Header } from '../header.tsx';

const slideDurationMs = 200;

interface Props {
  title: string;
  // Render-prop children receive requestBack, which plays the slide-out animation
  // before popping the history stack (same exit as the header back button).
  children: ReactNode | ((requestBack: () => void) => ReactNode);
  onInfoClick?: () => void; // extra Header icon (e.g. Help's ⓘ → About)
  onExit?: () => void; // invoked just before the back navigation commits
}

// Full-screen wrapper for routed settings dialog pages (Household, About, Help).
// Slides in from the right on mount; the back button slides it out to the right,
// then pops the history stack once the animation completes.
export const DialogPage = ({ title, children, onInfoClick, onExit }: Props) => {
  const navigate = useNavigate();
  const [isExiting, setIsExiting] = useState(false);
  const hasNavigatedBackRef = useRef(false);

  const finishExit = useCallback(async () => {
    if (hasNavigatedBackRef.current) {
      return;
    }
    hasNavigatedBackRef.current = true;
    onExit?.();
    await goBack(navigate);
  }, [navigate, onExit]);

  const onBack = useCallback(() => {
    setIsExiting(true);
  }, []);

  // Fallback in case the animationend event never fires (e.g. reduced motion)
  useEffect(() => {
    if (!isExiting) {
      return;
    }
    const timeout = setTimeout(() => void finishExit(), slideDurationMs + 100);
    return () => clearTimeout(timeout);
  }, [isExiting, finishExit]);

  const onAnimationEnd = useCallback(
    (event: AnimationEvent) => {
      if (!isExiting || event.target !== event.currentTarget) {
        return;
      }
      void finishExit();
    },
    [isExiting, finishExit],
  );

  return (
    <DialogWrapper $exiting={isExiting} onAnimationEnd={onAnimationEnd}>
      <Header title={title} onBack={onBack} onInfoClick={onInfoClick} />
      <InnerPageContent>{typeof children === 'function' ? children(onBack) : children}</InnerPageContent>
    </DialogWrapper>
  );
};

const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const DialogWrapper = styled(OuterPageContent)<{ $exiting: boolean }>`
  background-color: var(--color-background);
  animation: ${({ $exiting }) => ($exiting ? slideOutToRight : slideInFromRight)} ${slideDurationMs}ms ease-out;
  animation-fill-mode: ${({ $exiting }) => ($exiting ? 'forwards' : 'none')};
`;

interface SubViewProps {
  onClose: () => void;
  children: (close: () => void) => ReactNode;
}

// Covers a dialog page with a locally hosted sub-view (e.g. Choose Church from Household).
// Slides in on mount; children receive `close`, which plays the slide-out animation and
// then calls onClose so the host can unmount the overlay. Route all back/done UI through
// `close` — calling onClose directly would skip the exit animation.
export const SubViewOverlay = ({ onClose, children }: SubViewProps) => {
  const [isExiting, setIsExiting] = useState(false);
  const hasClosedRef = useRef(false);

  const finishClose = useCallback(() => {
    if (hasClosedRef.current) {
      return;
    }
    hasClosedRef.current = true;
    onClose();
  }, [onClose]);

  const close = useCallback(() => {
    setIsExiting(true);
  }, []);

  // Fallback in case the animationend event never fires (e.g. reduced motion)
  useEffect(() => {
    if (!isExiting) {
      return;
    }
    const timeout = setTimeout(finishClose, slideDurationMs + 100);
    return () => clearTimeout(timeout);
  }, [isExiting, finishClose]);

  const onAnimationEnd = useCallback(
    (event: AnimationEvent) => {
      if (!isExiting || event.target !== event.currentTarget) {
        return;
      }
      finishClose();
    },
    [isExiting, finishClose],
  );

  return (
    <SubViewWrapper $exiting={isExiting} onAnimationEnd={onAnimationEnd}>
      {children(close)}
    </SubViewWrapper>
  );
};

const SubViewWrapper = styled(OuterPageContent)<{ $exiting: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 2;
  background-color: var(--color-background);
  animation: ${({ $exiting }) => ($exiting ? slideOutToRight : slideInFromRight)} ${slideDurationMs}ms ease-out;
  animation-fill-mode: ${({ $exiting }) => ($exiting ? 'forwards' : 'none')};
`;
